import { useMemo, useState } from 'react'
import { FilterBar, EMPTY_FILTERS, ALL, type Filters } from '@/components/filters/FilterBar'
import { useMonthCursor } from '@/hooks/useMonthCursor'
import { matchesQuery } from '@/lib/text'
import { categoryScope } from '@/components/filters/applyFilters'
import type { ComboOption } from '@/components/ui/combobox'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  Plus,
  Pencil,
  Trash2,
  Repeat,
  Ban,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Clock,
  Infinity as InfinityIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { MonthPicker } from '@/components/ui/month-picker'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { PageHeader } from '@/components/ui/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { SortHead, useTableSort, cmp } from '@/components/ui/sortable-head'
import {
  useRecurringRules,
  useDeleteRecurring,
  useIncludeRecurring,
  useSkipRecurring,
  type RecurringRule,
} from '@/hooks/useRecurring'
import { useTransactions } from '@/hooks/useTransactions'
import { useCategories, buildTree } from '@/hooks/useCategories'
import { useAccounts } from '@/hooks/useAccounts'
import { useConfirm } from '@/context/ConfirmProvider'
import { formatCurrency } from '@/lib/currency'
import { accountIcon } from '@/lib/accountIcon'
import { accountComboOptions } from '@/lib/accountOptions'
import { monthOccurrenceStatus } from '@/lib/recurring'
import { defaultDateForMonth } from '@/components/transactions/TransactionFormDialog'
import { RecurringFormDialog } from '@/components/recurring/RecurringFormDialog'
import { IncludeRecurringDialog } from '@/components/recurring/IncludeRecurringDialog'
import { useT, type TFn } from '@/i18n/I18nProvider'
import { useTranslateCategory } from '@/lib/categoryDisplay'

// Rótulo amigável considerando o intervalo: mensal a cada 3/6 = Trimestral/Semestral.
function freqLabel(rule: RecurringRule, tr: TFn): string {
  if (rule.frequency === 'monthly' && rule.interval === 3) return tr('rec.freq.quarterly')
  if (rule.frequency === 'monthly' && rule.interval === 6) return tr('rec.freq.semiannual')
  const base = tr(`rec.freq.${rule.frequency}`)
  return rule.interval > 1 ? tr('rec.freq.withInterval', { base, n: rule.interval }) : base
}
function monthLabel(d: Date) {
  return format(d, "MMMM 'de' yyyy", { locale: ptBR })
}
function isCurrentMonth(d: Date) {
  const n = new Date()
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth()
}
function monthBounds(d: Date) {
  const iso = (x: Date) => x.toISOString().slice(0, 10)
  return { from: iso(new Date(d.getFullYear(), d.getMonth(), 1)), to: iso(new Date(d.getFullYear(), d.getMonth() + 1, 0)) }
}
function fmtBr(isoDate: string) {
  const [y, m, d] = isoDate.split('-')
  return `${d}/${m}/${y}`
}

export function RecurringPage() {
  const { t: tr } = useT()
  const tc = useTranslateCategory()
  const { data: rules, isLoading } = useRecurringRules()
  const { data: categories } = useCategories()
  const { data: accounts } = useAccounts(true)
  const del = useDeleteRecurring()
  const include = useIncludeRecurring()
  const skip = useSkipRecurring()
  const confirm = useConfirm()

  const [cursor, setCursor] = useMonthCursor()
  const range = useMemo(() => monthBounds(cursor), [cursor])
  const { data: monthTxs } = useTransactions(range)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [includeOpen, setIncludeOpen] = useState(false)
  const [editing, setEditing] = useState<RecurringRule | null>(null)

  const catName = useMemo(() => {
    const map = new Map((categories ?? []).map((c) => [c.id, c.name]))
    return (id: string | '') => (id ? (map.get(id) ?? '—') : '—')
  }, [categories])

  // Uma linha por OCORRÊNCIA da regra no mês, com status "incluída" ou não.
  const occurrences = useMemo(() => {
    const y = cursor.getFullYear()
    const m = cursor.getMonth()
    const rows: { rule: RecurringRule; date: string; included: boolean }[] = []
    for (const r of rules ?? []) {
      for (const o of monthOccurrenceStatus(r, monthTxs ?? [], y, m)) {
        rows.push({ rule: r, date: o.date, included: o.included })
      }
    }
    return rows.sort((a, b) => a.date.localeCompare(b.date))
  }, [rules, monthTxs, cursor])

  const disponiveis = occurrences.filter((o) => !o.included)

  // FILTROS. Sem status (aqui o estado é "incluída/disponível", já visível na lista).
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const accountOptions: ComboOption[] = useMemo(
    () => accountComboOptions(accounts),
    [accounts],
  )
  const categoryOptions: ComboOption[] = useMemo(
    () =>
      (['expense', 'income', 'investment'] as const).flatMap((k) =>
        buildTree(categories ?? [], k).flatMap(({ parent, children }) => [
          { value: parent.id, label: tc(parent.name), bold: true } as ComboOption,
          ...children.map((c): ComboOption => ({ value: c.id, label: tc(c.name), depth: 1, keywords: parent.name })),
        ]),
      ),
    [categories],
  )

  // Categoria PAI traz os FILHOS junto.
  const catScope = useMemo(() => categoryScope(filters.categoryId, categories), [filters.categoryId, categories])

  type RKey = 'status' | 'desc' | 'freq' | 'account' | 'category' | 'venc' | 'valor'
  const { sort, toggle } = useTableSort<RKey>('venc', 'asc', ['valor'])
  const sortedOcc = useMemo(() => {
    const val = (o: { rule: RecurringRule; date: string; included: boolean }): string | number => {
      const t = o.rule.template
      switch (sort.key) {
        case 'status':
          return o.included ? 1 : 0
        case 'desc':
          return (t.description ?? '').toLowerCase()
        case 'freq':
          return freqLabel(o.rule, tr)
        case 'account':
          return o.rule.account?.name ?? ''
        case 'category':
          return catName(t.category_id)
        case 'venc':
          return o.date
        case 'valor':
          return (t.kind === 'expense' ? -1 : 1) * Number(t.amount)
      }
    }
    return occurrences
      .filter((o) => {
        const t = o.rule.template
        if (filters.accountId !== ALL && o.rule.account_id !== filters.accountId) return false
        if (catScope && !(t.category_id && catScope.has(t.category_id))) return false
        if (filters.kind !== ALL && t.kind !== filters.kind) return false
        // Aqui "status" é: já virou transação (incluída) x ainda não lançada.
        if (filters.status === 'included' && !o.included) return false
        if (filters.status === 'notIncluded' && o.included) return false
        return matchesQuery([t.description, catName(t.category_id ?? ''), o.rule.account?.name], filters.q)
      })
      .sort((a, b) => cmp(val(a), val(b), sort.dir))
  }, [occurrences, sort, catName, filters, catScope, tr])

  function shiftMonth(delta: number) {
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1))
  }
  function openNew() {
    setEditing(null)
    setDialogOpen(true)
  }
  function openEdit(r: RecurringRule) {
    setEditing(r)
    setDialogOpen(true)
  }
  async function remove(r: RecurringRule) {
    const ok = await confirm({
      title: tr('rec.confirm.deleteTitle'),
      description: tr('rec.confirm.deleteDescription'),
      confirmText: tr('common.delete'),
      tone: 'destructive',
    })
    if (!ok) return
    try {
      await del.mutateAsync({ id: r.id })
      toast.success(tr('rec.toast.deleted'))
    } catch (e) {
      toast.error(tr('rec.toast.deleteError'), { description: (e as Error).message })
    }
  }
  async function includeOne(rule: RecurringRule, date: string) {
    try {
      await include.mutateAsync([{ ruleId: rule.id, date, template: rule.template }])
      toast.success(tr('rec.toast.includedPending'))
    } catch (e) {
      toast.error(tr('rec.toast.includeError'), { description: (e as Error).message })
    }
  }
  // "Não aplicável neste mês": marca a ocorrência como 0 (paga) — sai do orçado
  // sem virar gasto. Só faz sentido nas DISPONÍVEIS (as já incluídas têm valor).
  async function skipOne(rule: RecurringRule, date: string) {
    try {
      await skip.mutateAsync([{ ruleId: rule.id, date, template: rule.template }])
      toast.success(tr('rec.toast.markedNA'))
    } catch (e) {
      toast.error(tr('rec.toast.markError'), { description: (e as Error).message })
    }
  }
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title={tr('rec.title')}
        subtitle={tr('rec.subtitle')}
        actions={
          <>
            <Button variant="outline" onClick={() => setIncludeOpen(true)} disabled={disponiveis.length === 0}>
              <Plus className="size-4" /> {tr('rec.includeRecurring', { count: disponiveis.length })}
            </Button>
            <Button onClick={openNew}>
              <Plus className="size-4" /> {tr('rec.newRecurring')}
            </Button>
          </>
        }
      />

      {/* Navegador de mês */}
      <div className="flex items-center justify-between rounded-lg border p-2">
        <Button variant="ghost" size="icon" onClick={() => shiftMonth(-1)} aria-label={tr('rec.prevMonth')}>
          <ChevronLeft className="size-5" />
        </Button>
        <div className="text-center">
          <MonthPicker value={cursor} onChange={setCursor} />
          <div className="text-muted-foreground text-xs">
            {tr('rec.monthSummary', {
              included: occurrences.length - disponiveis.length,
              available: disponiveis.length,
            })}
          </div>
          {!isCurrentMonth(cursor) && (
            <button className="text-primary text-xs hover:underline" onClick={() => setCursor(new Date())}>
              {tr('rec.backToCurrentMonth')}
            </button>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={() => shiftMonth(1)} aria-label={tr('rec.nextMonth')}>
          <ChevronRight className="size-5" />
        </Button>
      </div>

      <FilterBar
        filters={filters}
        onChange={setFilters}
        accounts={accountOptions}
        categories={categoryOptions}
        statusAllLabel={tr('rec.filter.statusAll')}
        statusOptions={[
          { value: 'notIncluded', label: tr('rec.filter.notIncluded') },
          { value: 'included', label: tr('rec.filter.included') },
        ]}
        placeholder={tr('rec.filter.searchPlaceholder')}
      />

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="text-muted-foreground p-6">{tr('common.loading')}</p>
          ) : sortedOcc.length > 0 ? (
            <>
            <div className="hidden overflow-x-auto md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortHead label={tr('rec.col.status')} k="status" sort={sort} onToggle={toggle} className="w-10" />
                    <SortHead label={tr('rec.col.description')} k="desc" sort={sort} onToggle={toggle} />
                    <SortHead label={tr('rec.col.frequency')} k="freq" sort={sort} onToggle={toggle} />
                    <SortHead label={tr('rec.col.account')} k="account" sort={sort} onToggle={toggle} />
                    <SortHead label={tr('rec.col.category')} k="category" sort={sort} onToggle={toggle} />
                    <SortHead label={tr('rec.col.dueDate')} k="venc" sort={sort} onToggle={toggle} />
                    <SortHead label={tr('rec.col.amount')} k="valor" sort={sort} onToggle={toggle} className="text-right" />
                    <TableHead className="w-44" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedOcc.map((o) => {
                    const t = o.rule.template
                    const isExpense = t.kind === 'expense'
                    return (
                      <TableRow key={o.rule.id + '|' + o.date} className={o.included ? '' : 'bg-muted/20'}>
                        <TableCell>
                          {o.included ? (
                            <CheckCircle2 className="text-success size-4" aria-label={tr('rec.status.included')} />
                          ) : (
                            <Clock className="text-muted-foreground size-4" aria-label={tr('rec.status.available')} />
                          )}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate font-medium">
                          <span className="inline-flex items-center gap-1.5">
                            <Repeat className="text-primary size-3.5 shrink-0" />
                            {t.description || <span className="text-muted-foreground">{tr('rec.noDescription')}</span>}
                          </span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {freqLabel(o.rule, tr)}
                          {o.rule.max_occurrences ? (
                            <span className="text-muted-foreground ml-1 text-xs">· {o.rule.max_occurrences}×</span>
                          ) : !o.rule.end_date ? (
                            <InfinityIcon className="text-muted-foreground ml-1 inline size-3" aria-label={tr('rec.noEnd')} />
                          ) : null}
                        </TableCell>
                        <TableCell className="max-w-[130px] truncate">
                          {o.rule.account ? `${accountIcon(o.rule.account)} ${o.rule.account.name}` : '—'}
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate">{tc(catName(t.category_id))}</TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums">{fmtBr(o.date)}</TableCell>
                        <TableCell className={`text-right tabular-nums whitespace-nowrap ${isExpense ? '' : 'text-success'}`}>
                          {isExpense ? '-' : '+'}
                          {formatCurrency(t.amount, t.currency)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {o.included ? (
                              <Badge variant="secondary" className="text-success gap-1">
                                <CheckCircle2 className="size-3" /> {tr('rec.status.included')}
                              </Badge>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={include.isPending}
                                  onClick={() => includeOne(o.rule, o.date)}
                                >
                                  {tr('rec.include')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-muted-foreground hover:text-destructive gap-1"
                                  disabled={skip.isPending}
                                  title={tr('rec.naTitle')}
                                  onClick={() => skipOne(o.rule, o.date)}
                                >
                                  <Ban className="size-3.5" /> {tr('rec.na')}
                                </Button>
                              </>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => openEdit(o.rule)} aria-label={tr('common.edit')}>
                              <Pencil className="size-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => remove(o.rule)} aria-label={tr('common.delete')}>
                              <Trash2 className="text-destructive size-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Mesma lista em cartões no mobile (a tabela de ~8 colunas não cabe). */}
            <div className="space-y-2 p-3 md:hidden">
              {sortedOcc.map((o) => {
                const t = o.rule.template
                const isExpense = t.kind === 'expense'
                return (
                  <div
                    key={o.rule.id + '|' + o.date}
                    className={`rounded-xl border p-3 ${o.included ? '' : 'bg-muted/20'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="inline-flex min-w-0 items-center gap-1.5 font-medium">
                        {o.included ? (
                          <CheckCircle2 className="text-success size-4 shrink-0" aria-label={tr('rec.status.included')} />
                        ) : (
                          <Clock className="text-muted-foreground size-4 shrink-0" aria-label={tr('rec.status.available')} />
                        )}
                        <Repeat className="text-primary size-3.5 shrink-0" />
                        <span className="truncate">
                          {t.description || <span className="text-muted-foreground">{tr('rec.noDescription')}</span>}
                        </span>
                      </span>
                      <span
                        className={`shrink-0 font-bold tabular-nums whitespace-nowrap ${isExpense ? '' : 'text-success'}`}
                      >
                        {isExpense ? '-' : '+'}
                        {formatCurrency(t.amount, t.currency)}
                      </span>
                    </div>
                    <div className="text-muted-foreground mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                      <span>{o.rule.account ? `${accountIcon(o.rule.account)} ${o.rule.account.name}` : '—'}</span>
                      <span>{tc(catName(t.category_id))}</span>
                      <span className="inline-flex items-center gap-1">
                        {freqLabel(o.rule, tr)}
                        {o.rule.max_occurrences ? (
                          <span>· {o.rule.max_occurrences}×</span>
                        ) : !o.rule.end_date ? (
                          <InfinityIcon className="size-3" aria-label={tr('rec.noEnd')} />
                        ) : null}
                      </span>
                      <span className="tabular-nums">{tr('rec.dueOn', { date: fmtBr(o.date) })}</span>
                      <span>{o.included ? tr('rec.status.includedChip') : tr('rec.status.availableChip')}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-end gap-1">
                      {o.included ? (
                        <Badge variant="secondary" className="text-success gap-1">
                          <CheckCircle2 className="size-3" /> {tr('rec.status.included')}
                        </Badge>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={include.isPending}
                            onClick={() => includeOne(o.rule, o.date)}
                          >
                            {tr('rec.include')}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-muted-foreground hover:text-destructive gap-1"
                            disabled={skip.isPending}
                            title={tr('rec.naTitle')}
                            onClick={() => skipOne(o.rule, o.date)}
                          >
                            <Ban className="size-3.5" /> {tr('rec.na')}
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => openEdit(o.rule)} aria-label={tr('common.edit')}>
                        <Pencil className="size-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(o.rule)} aria-label={tr('common.delete')}>
                        <Trash2 className="text-destructive size-4" />
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
            </>
          ) : (
            <EmptyState
              icon={Repeat}
              title={tr('rec.empty.title', { month: monthLabel(cursor) })}
              description={tr('rec.empty.description')}
              action={
                <Button onClick={openNew}>
                  <Plus className="size-4" /> {tr('rec.newRecurring')}
                </Button>
              }
            />
          )}
        </CardContent>
      </Card>

      <RecurringFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        rule={editing}
        defaultDate={defaultDateForMonth(cursor)}
      />
      <IncludeRecurringDialog
        open={includeOpen}
        onOpenChange={setIncludeOpen}
        year={cursor.getFullYear()}
        month0={cursor.getMonth()}
        title={tr('rec.includeDialog.titleMonth', { month: monthLabel(cursor) })}
      />
    </div>
  )
}
